import { AccountInfo } from '@azure/msal-common';
import { AUTH_TYPE, AuthType, ROLE_TYPE } from 'interfaces/authTypes';
import { HpAction, HpTemplateActionTypes } from '../actions/actionTypes';
import { getInitialState, HpAuthenticationState } from './initialState';
import { ReducerProcessor } from './reducerProcessor';
import {
  CustomerInsuranceSummaryActionPayload,
  CustomerInsuranceSummaryErrorPayload,
  CustomerInsuranceSummaryResponse
} from 'actions/getCustomerInsuranceSummaryAction';

const processor = new ReducerProcessor<HpAuthenticationState, HpTemplateActionTypes>('User');

processor.add<HpAction<AccountInfo>>('RECEIVE_AUTHENTICATED_USER', (state, action) => {
  const { username, name } = action.payload as AccountInfo;
  return {
    ...state,
    email: username,
    name: name as string
  };
});

processor.add<HpAction<CustomerInsuranceSummaryErrorPayload>>('CUSTOMER_INSURANCE_SUMMARY_ERROR', (state, action) => {
  const { authType, error } = action.payload as CustomerInsuranceSummaryErrorPayload;

  if (authType === AUTH_TYPE.AGENT && error === 'No data found for user') {
    return {
      ...state,
      isAuthenticated: true
    };
  }

  const isNoTokenError = error === 'There is no auth token in the session.';

  if (isNoTokenError) {
    return state; // Don't show an error just redirect to login
  }

  return {
    ...state,
    authLinkError: error,
    isTokenError: isNoTokenError
  };
});

processor.add<HpAction<CustomerInsuranceSummaryResponse>>('CUSTOMER_INSURANCE_SUMMARY_SUCCESS', (state, action) => {
  const { policies } = action.payload as CustomerInsuranceSummaryResponse;
  const { isAgent } = state;

  if (isAgent) {
    return {
      ...state,
      isAuthenticated: true
    };
  }

  const emailAddress = policies && policies.length > 0 ? policies[0].email : '';

  return {
    ...state,
    isAuthenticated: true,
    email: emailAddress
  };
});

processor.add<HpAction<{ urlAuthType: AuthType }>>('CUSTOMER_INSURANCE_SUMMARY_LOADING', (state, action) => {
  const currentAuthType = state.authType;
  const actionAuthType = (action.payload as { urlAuthType: AuthType }).urlAuthType;

  if (
    currentAuthType === AUTH_TYPE.NONE ||
    (currentAuthType === AUTH_TYPE.AUTH && actionAuthType === AUTH_TYPE.PASSBACK)
  ) {
    return {
      ...state,
      authType: actionAuthType
    };
  }

  return state;
});

processor.add('SIGN_OUT_LOADING', (state) => {
  return {
    ...state,
    isAuthenticated: false,
    isAgentAuthenticatedOrCustomer: false,
    isAgentAdmin: false,
    isAgent: false,
    authType: AUTH_TYPE.NONE,
    email: '',
    name: ''
  };
});

processor.add<HpAction<AccountInfo>>('AZURE_AD_AUTHENTICATION_SUCCESS', (state, action) => {
  const account = action.payload;
  const isAgent = account?.idTokenClaims?.roles?.includes(ROLE_TYPE.AGENT) || false;

  if (isAgent) {
    const isAgentAdmin = account?.idTokenClaims?.roles?.includes(ROLE_TYPE.AGENT_ADMIN) || false;
    return {
      ...state,
      isAgentAuthenticatedOrCustomer: true,
      isAgentAdmin,
      isAgent: true
    };
  }

  return {
    ...state,
    isAgentAuthenticatedOrCustomer: true
  };
});

export function authenticationReducer(
  state: HpAuthenticationState = getInitialState().authentication,
  action: HpAction<string | AccountInfo | CustomerInsuranceSummaryActionPayload>
): HpAuthenticationState {
  return processor.process(state, action);
}
